import { api, type DocumentMeta, type Folder, type Workspace, type SearchResult } from '../services/api';
import { resourcesApi, type Resource } from '../services/resources-api';
import { ContextMenuManager } from './ContextMenuManager';

// Local types for knowledge items (migrated from knowledge-api)
interface KnowledgeSearchResult {
  score: number;
  metadata: {
    docId: string;
    source: string;
    title: string;
  };
}

interface KnowledgeListItem {
  id: string;
  title: string;
  source: string;
  excerpt?: string;
  url?: string;
  categories?: string[];
  projectId?: string;
  intent?: string;
  updatedAt?: number;
}

interface KnowledgeItemDetail extends KnowledgeListItem {
  content?: string;
}

export type DocSelectCallback = (doc: DocumentMeta) => void;
export type DocDeleteCallback = (docId: string) => void;
export type DocRenameCallback = (docId: string, newTitle: string) => void;
export type DocFavoriteCallback = (docId: string, isFavorite: boolean) => void;
export type DocMoveCallback = (docId: string, folderId: string | null) => void;
export type DocArchiveCallback = (docId: string, isArchived: boolean) => void;
export type DocKnowledgeCallback = (docId: string, enabled: boolean) => void;
export type DocWorkspaceCallback = (docId: string, workspaceId: string) => void;
export type FolderDeleteCallback = (folderId: string) => void;
export type FolderRenameCallback = (folderId: string, name: string) => void;
export type WorkspaceChangeCallback = (workspaceId: string) => void;
export type TabChangeCallback = (tab: string) => void;
export type KnowledgeItemOpenCallback = (item: KnowledgeItemDetail) => void;

export class Sidebar {
  private docListEl: HTMLElement;
  private tagsFilterEl: HTMLElement;
  private searchInputEl: HTMLInputElement;
  private relatedDocsPanelEl: HTMLElement;
  private relatedDocsListEl: HTMLElement;
  private documents: DocumentMeta[] = [];
  private folders: Folder[] = [];
  private tags: string[] = [];
  private activeDocId: string | null = null;
  private activeTag: string | null = null;
  private searchQuery: string = '';
  private expandedFolders: Set<string> = new Set();
  private showArchived: boolean = false;
  private workspaces: Workspace[] = [];
  private activeWorkspaceId: string = 'default';
  private relatedDocs: KnowledgeSearchResult[] = [];
  private activeTab: string = 'notes';
  private searchResults: SearchResult[] | null = null;
  private isSearching: boolean = false;
  private selectedProject: string | null = null;
  private selectedResourceCategory: string | null = null;
  private expandedCategoryGroups: Set<string> = new Set(); // For hierarchical categories
  private onResourceCategorySelect: ((category: string) => void) | null = null;
  private onIntentFilter: ((intent: string | null) => void) | null = null;

  // Knowledge tab
  private knowledgeItems: KnowledgeListItem[] = [];
  private isLoadingKnowledge: boolean = false;

  private onDocSelect: DocSelectCallback | null = null;
  private onDocDelete: DocDeleteCallback | null = null;
  private onDocRename: DocRenameCallback | null = null;
  private onDocFavorite: DocFavoriteCallback | null = null;
  private onDocMove: DocMoveCallback | null = null;
  private onDocArchive: DocArchiveCallback | null = null;
  private onDocKnowledge: DocKnowledgeCallback | null = null;
  private onDocWorkspace: DocWorkspaceCallback | null = null;
  private onWorkspaceChange: WorkspaceChangeCallback | null = null;
  private onTabChange: TabChangeCallback | null = null;
  private onKnowledgeItemOpen: KnowledgeItemOpenCallback | null = null;
  private onProjectSelect: ((projectName: string) => void) | null = null;

  // Context menu manager
  private contextMenuManager: ContextMenuManager;

  // Workspaces list
  private workspacesListEl: HTMLElement | null = null;
  private hashtagsListEl: HTMLElement | null = null;

  // PARA folder elements
  private favoritesListEl: HTMLElement | null = null;
  private folderProjectsEl: HTMLElement | null = null;
  private folderAreasEl: HTMLElement | null = null;
  private folderResourcesEl: HTMLElement | null = null;
  private folderArchivesEl: HTMLElement | null = null;

  constructor() {
    // Legacy elements (may not exist in new layout)
    this.docListEl = document.getElementById('doc-list') || document.createElement('div');
    this.tagsFilterEl = document.getElementById('tags-filter') || document.createElement('div');
    this.searchInputEl = document.getElementById('search-input') as HTMLInputElement;
    this.relatedDocsPanelEl = document.getElementById('related-docs-panel') || document.createElement('div');
    this.relatedDocsListEl = document.getElementById('related-docs-list') || document.createElement('div');

    // Workspaces and Hashtags
    this.workspacesListEl = document.getElementById('workspaces-list');
    this.hashtagsListEl = document.getElementById('hashtags-list');

    // New PARA folder elements
    this.favoritesListEl = document.getElementById('favorites-list');
    this.folderProjectsEl = document.getElementById('folder-projects');
    this.folderAreasEl = document.getElementById('folder-areas');
    this.folderResourcesEl = document.getElementById('folder-resources');
    this.folderArchivesEl = document.getElementById('folder-archives');

    // Cargar workspace activo guardado
    this.activeWorkspaceId = localStorage.getItem('notepad_active_workspace') || 'default';

    // Initialize context menu manager
    this.contextMenuManager = new ContextMenuManager();

    this.setupEventListeners();
    this.setupQuickTagListeners();
    this.setupTabListeners();
    this.setupKnowledgeTabListeners();
    this.setupTreeFolderListeners();
    this.setupQuickFilters();
    this.loadExpandedFolders();
  }

  private setupQuickFilters(): void {
    const filterBtns = document.querySelectorAll('.quick-filter-btn');

    filterBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const intent = (btn as HTMLElement).dataset.intent;
        if (!intent) return;

        // Toggle active state
        const wasActive = btn.classList.contains('active');
        filterBtns.forEach(b => b.classList.remove('active'));

        let selectedIntent: string | null = null;

        if (wasActive) {
          // Deactivate - show all
          selectedIntent = null;
        } else {
          // Activate filter
          btn.classList.add('active');
          selectedIntent = intent;
        }

        // Switch to knowledge tab and apply filter
        if (this.activeTab !== 'knowledge') {
          this.switchTab('knowledge');
        }

        // Call the intent filter callback
        if (this.onIntentFilter) {
          this.onIntentFilter(selectedIntent);
        }
      });
    });
  }

  public updateIntentBadges(): void {
    const readLaterCount = this.knowledgeItems.filter(i => i.intent === 'read_later').length;
    const applyLaterCount = this.knowledgeItems.filter(i => i.intent === 'implement_later').length;

    const readBadge = document.getElementById('badge-read-later');
    const applyBadge = document.getElementById('badge-apply-later');

    if (readBadge) {
      readBadge.textContent = String(readLaterCount);
      readBadge.classList.toggle('hidden', readLaterCount === 0);
    }

    if (applyBadge) {
      applyBadge.textContent = String(applyLaterCount);
      applyBadge.classList.toggle('hidden', applyLaterCount === 0);
    }
  }

  private setupQuickTagListeners(): void {
    const quickTags = document.querySelectorAll('.quick-tag');
    quickTags.forEach(tag => {
      tag.addEventListener('click', () => {
        const tagText = tag.textContent?.replace('#', '') || '';
        quickTags.forEach(t => t.classList.remove('active'));
        if (this.activeTag === tagText) {
          this.activeTag = null;
        } else {
          tag.classList.add('active');
          this.activeTag = tagText;
        }
        this.renderPARAFolders();
      });
    });
  }

  private setupTabListeners(): void {
    // Listen to both .sidebar-tab and .icon-btn with data-tab
    const tabs = document.querySelectorAll('.sidebar-tab, .icon-btn[data-tab]');
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabId = tab.getAttribute('data-tab');
        if (tabId) {
          this.switchTab(tabId);
        }
      });
    });
  }

  public switchTab(tabId: string, silent: boolean = false): void {
    // Guard: prevent infinite loop when syncing with ViewManager
    if (this.activeTab === tabId) return;

    this.activeTab = tabId;

    // Update tab buttons
    document.querySelectorAll('.sidebar-tab, .icon-btn[data-tab]').forEach(tab => {
      tab.classList.toggle('active', tab.getAttribute('data-tab') === tabId);
    });

    // Update tab content (sidebar)
    document.querySelectorAll('.sidebar-tab-content').forEach(content => {
      content.classList.toggle('active', content.id === `tab-${tabId}`);
    });

    // Update main views visibility
    const mainViews = {
      'inbox': 'inbox-view',
      'notes': 'editor-view',
      'knowledge': 'knowledge-view',
      'projects': 'projects-view',
      'outreach': 'outreach-view',
      'patterns': 'patterns-view',
      'profiles': 'profiles-view',
      'settings': 'settings-view',
      'vault': 'vault-view',
      'classification': 'classification-queue-view',
      'docs': 'editor-view' // Legacy support
    };

    // Hide all main views
    document.querySelectorAll('.main-view').forEach(view => {
      view.classList.add('hidden');
    });

    // Show the active main view
    const viewId = mainViews[tabId as keyof typeof mainViews] || 'editor-view';
    const viewEl = document.getElementById(viewId);
    if (viewEl) {
      viewEl.classList.remove('hidden');
    }

    // Load items when switching tabs
    if (tabId === 'knowledge' && this.knowledgeItems.length === 0) {
      this.loadKnowledgeItems();
    }

    // Notify callback (skip if silent - used by ViewManager sync)
    if (this.onTabChange && !silent) {
      this.onTabChange(tabId);
    }
  }

  public getActiveTab(): string {
    return this.activeTab;
  }

  onTabChangeCallback(callback: TabChangeCallback): void {
    this.onTabChange = callback;
  }

  private setupEventListeners(): void {
    // Search on Enter key
    this.searchInputEl.addEventListener('keydown', async (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        await this.executeSearch();
      } else if (e.key === 'Escape') {
        this.clearSearch();
      }
    });
  }

  private async executeSearch(): Promise<void> {
    this.searchQuery = this.searchInputEl.value.toLowerCase().trim();

    if (this.searchQuery.length >= 2) {
      await this.performServerSearch(this.searchQuery);
    } else if (this.searchQuery.length === 0) {
      this.clearSearch();
    }
  }

  private clearSearch(): void {
    this.searchInputEl.value = '';
    this.searchQuery = '';
    this.searchResults = null;
    this.renderDocList();
  }

  private async performServerSearch(query: string): Promise<void> {
    this.isSearching = true;
    this.renderDocList(); // Show loading state

    try {
      const response = await api.search(query);
      if (response.success && response.data) {
        this.searchResults = response.data;
      } else {
        this.searchResults = [];
      }
    } catch (error) {
      console.error('Search failed:', error);
      this.searchResults = [];
    }

    this.isSearching = false;
    this.renderDocList();
  }

  private loadExpandedFolders(): void {
    const stored = localStorage.getItem('notepad_expanded_folders');
    if (stored) {
      try {
        this.expandedFolders = new Set(JSON.parse(stored));
      } catch (e) {
        this.expandedFolders = new Set();
      }
    }
  }

  private saveExpandedFolders(): void {
    localStorage.setItem('notepad_expanded_folders', JSON.stringify([...this.expandedFolders]));
  }

  async loadDocuments(): Promise<void> {
    const response = await api.listDocuments();
    if (response.success && response.data) {
      this.documents = response.data;
      this.renderDocList();
      this.renderPARAFolders();
      this.renderHashtags();
    }
  }

  async loadFolders(): Promise<void> {
    const response = await api.listFolders();
    if (response.success && response.data) {
      this.folders = response.data;
      this.contextMenuManager.setFolders(this.folders);
      this.renderDocList();
      this.renderPARAFolders();
    }
  }

  async loadTags(): Promise<void> {
    const response = await api.listTags();
    if (response.success && response.data) {
      this.tags = response.data;
      this.renderTags();
    }
  }

  async loadWorkspaces(): Promise<void> {
    const response = await api.listWorkspaces();
    if (response.success && response.data) {
      this.workspaces = response.data;
      this.contextMenuManager.setWorkspaces(this.workspaces);
      this.renderWorkspaceSelector();
      this.renderWorkspacesList();
    }
  }

  async refresh(): Promise<void> {
    await Promise.all([this.loadDocuments(), this.loadFolders(), this.loadTags(), this.loadWorkspaces()]);
  }

  private filterDocuments(): DocumentMeta[] {
    let filtered = this.documents;

    // Filtrar por workspace activo
    filtered = filtered.filter(doc =>
      !doc.workspaceId || doc.workspaceId === this.activeWorkspaceId
    );

    // Filtrar archivados (por defecto ocultos)
    if (!this.showArchived) {
      filtered = filtered.filter(doc => !doc.isArchived);
    }

    // Ocultar Resources del listado de Documents (solo mostrar si está abierto)
    filtered = filtered.filter(doc =>
      doc.type !== 'Resource' || doc.id === this.activeDocId
    );

    if (this.activeTag) {
      filtered = filtered.filter(doc =>
        doc.tags.some(t => t.toLowerCase() === this.activeTag?.toLowerCase())
      );
    }

    if (this.searchQuery) {
      filtered = filtered.filter(doc =>
        doc.title.toLowerCase().includes(this.searchQuery) ||
        doc.tags.some(t => t.toLowerCase().includes(this.searchQuery))
      );
    }

    return [...filtered].sort((a, b) => b.updated_at - a.updated_at);
  }

  private renderDocList(): void {
    this.docListEl.innerHTML = '';

    // Show loading state
    if (this.isSearching) {
      const loadingEl = document.createElement('li');
      loadingEl.className = 'doc-item';
      loadingEl.innerHTML = '<span class="doc-item-title" style="color: var(--text-muted)">Buscando...</span>';
      this.docListEl.appendChild(loadingEl);
      return;
    }

    // Show server search results if available
    if (this.searchResults !== null) {
      this.renderSearchResults();
      return;
    }

    // Normal document list - ONLY documents, NO folders here
    const filtered = this.filterDocuments();

    if (filtered.length === 0) {
      const emptyEl = document.createElement('li');
      emptyEl.className = 'doc-item';
      emptyEl.innerHTML = '<span class="doc-item-title" style="color: var(--text-muted)">No documents</span>';
      this.docListEl.appendChild(emptyEl);
      return;
    }

    // Just show all documents sorted by date
    for (const doc of filtered) {
      this.docListEl.appendChild(this.createDocItem(doc));
    }
  }

  // New method to render documents in PARA folders
  private renderPARAFolders(): void {
    const filtered = this.filterDocuments();

    // Render Favorites
    if (this.favoritesListEl) {
      this.favoritesListEl.innerHTML = '';
      const favorites = filtered.filter(d => d.isFavorite);
      for (const doc of favorites) {
        const li = document.createElement('li');
        li.className = 'nav-item';
        li.innerHTML = `
          <span class="material-symbols-outlined" style="color: #fbbf24;">star</span>
          <span class="truncate">${doc.title || 'Sin título'}</span>
        `;
        li.addEventListener('click', () => {
          if (this.onDocSelect) this.onDocSelect(doc);
        });
        this.favoritesListEl!.appendChild(li);
      }
      if (favorites.length === 0) {
        this.favoritesListEl.innerHTML = '<li class="nav-item" style="color: var(--text-muted); font-size: 12px;">No favorites yet</li>';
      }
    }

    // PROJECTS: Show unique project names with color dots
    if (this.folderProjectsEl) {
      this.folderProjectsEl.innerHTML = '';

      // Get unique projects from documents
      const projects = this.getUniqueProjects();

      for (const projectName of projects) {
        const item = document.createElement('div');
        item.className = `para-item${projectName === this.selectedProject ? ' active' : ''}`;
        item.innerHTML = `
          <span class="para-item-dot"></span>
          <span class="para-item-name">${projectName}</span>
        `;
        item.addEventListener('click', () => {
          this.selectedProject = projectName;
          this.renderPARAFolders(); // Re-render to update active state
          if (this.onProjectSelect) {
            this.onProjectSelect(projectName);
          }
        });
        this.folderProjectsEl.appendChild(item);
      }

      if (projects.length === 0) {
        this.folderProjectsEl.innerHTML = '<div class="para-item" style="color: var(--text-muted); font-size: 12px;">No projects</div>';
      }
    }

    // AREAS: Empty for now (will be semi-fixed categories defined later)
    if (this.folderAreasEl) {
      this.folderAreasEl.innerHTML = '<div class="para-folder-item" style="color: var(--text-muted); font-size: 12px;">No areas defined</div>';
    }

    // RESOURCES: Hierarchical categories with expand/collapse
    if (this.folderResourcesEl) {
      this.folderResourcesEl.innerHTML = '';

      // Category structure: { name, subcategories?, icon }
      const CATEGORY_STRUCTURE: Array<{
        name: string;
        icon: string;
        subcategories?: string[];
      }> = [
        { name: 'Programming', icon: 'code' },
        {
          name: 'Content Creation',
          icon: 'edit_note',
          subcategories: [
            'Inspiration', 'Content Ideas', 'Tropicalizar', 'Hooks',
            'Frameworks', 'Loops', 'Stakes', 'CTA', 'Packages', 'Angles', 'Scripts'
          ]
        },
        { name: 'Personal Brand', icon: 'person' },
        { name: 'Business', icon: 'business' },
        { name: 'Marketing', icon: 'campaign' },
        { name: 'Paid Ads', icon: 'ads_click' },
        { name: 'Video & Recording', icon: 'videocam' },
        { name: 'AI Prompts', icon: 'psychology' },
        { name: 'Personal Dev', icon: 'self_improvement' },
        { name: 'Side Projects', icon: 'rocket_launch' },
        { name: 'GTM & AdsConcierge', icon: 'store' },
        { name: 'Journaling', icon: 'book' },
        {
          name: 'Datasets',
          icon: 'dataset',
          subcategories: [
            'Dataset/Ads', 'Dataset/Reels', 'Dataset/Stories', 'Dataset/Vlog', 'Dataset/Thumbnails'
          ]
        },
        { name: 'Uncategorized', icon: 'folder' }
      ];

      // Count documents per category
      const resourceCounts: Record<string, number> = {};
      for (const doc of this.documents) {
        if (doc.resourceCategory) {
          resourceCounts[doc.resourceCategory] = (resourceCounts[doc.resourceCategory] || 0) + 1;
        }
      }

      // Helper to get total count for a category group
      const getGroupCount = (cat: typeof CATEGORY_STRUCTURE[0]): number => {
        let total = resourceCounts[cat.name] || 0;
        if (cat.subcategories) {
          for (const sub of cat.subcategories) {
            total += resourceCounts[sub] || 0;
          }
        }
        return total;
      };

      // Render categories
      for (const cat of CATEGORY_STRUCTURE) {
        const hasChildren = cat.subcategories && cat.subcategories.length > 0;
        const isExpanded = this.expandedCategoryGroups.has(cat.name);
        const totalCount = getGroupCount(cat);
        const directCount = resourceCounts[cat.name] || 0;

        // Main category item
        const item = document.createElement('div');
        item.className = `para-folder-item resource-category${this.selectedResourceCategory === cat.name ? ' active' : ''}`;
        item.style.cssText = 'display: flex; align-items: center; cursor: pointer;';
        if (totalCount === 0) item.style.opacity = '0.5';

        item.innerHTML = `
          ${hasChildren ? `<span class="material-symbols-outlined" style="font-size: 12px; margin-right: 2px; transition: transform 0.2s; ${isExpanded ? 'transform: rotate(90deg);' : ''}">${isExpanded ? 'expand_more' : 'chevron_right'}</span>` : '<span style="width: 14px;"></span>'}
          <span class="material-symbols-outlined" style="font-size: 14px; margin-right: 6px;">${cat.icon}</span>
          ${cat.name}
          <span style="margin-left: auto; font-size: 11px; color: var(--text-muted);">${totalCount}</span>
        `;

        item.addEventListener('click', (e) => {
          if (hasChildren) {
            // Toggle expand/collapse
            if (this.expandedCategoryGroups.has(cat.name)) {
              this.expandedCategoryGroups.delete(cat.name);
            } else {
              this.expandedCategoryGroups.add(cat.name);
            }
            this.renderPARAFolders();
          } else {
            // Select category
            this.selectedResourceCategory = cat.name;
            this.renderPARAFolders();
            if (this.onResourceCategorySelect) {
              this.onResourceCategorySelect(cat.name);
            }
          }
        });

        this.folderResourcesEl.appendChild(item);

        // Render subcategories if expanded
        if (hasChildren && isExpanded && cat.subcategories) {
          for (const sub of cat.subcategories) {
            const subCount = resourceCounts[sub] || 0;
            const subItem = document.createElement('div');
            subItem.className = `para-folder-item resource-category subcategory${this.selectedResourceCategory === sub ? ' active' : ''}`;
            subItem.style.cssText = 'display: flex; align-items: center; cursor: pointer; padding-left: 28px; font-size: 12px;';
            if (subCount === 0) subItem.style.opacity = '0.5';

            subItem.innerHTML = `
              <span class="material-symbols-outlined" style="font-size: 12px; margin-right: 6px;">label</span>
              ${sub.replace('Dataset/', '')}
              <span style="margin-left: auto; font-size: 11px; color: var(--text-muted);">${subCount}</span>
            `;

            subItem.addEventListener('click', (e) => {
              e.stopPropagation();
              this.selectedResourceCategory = sub;
              this.renderPARAFolders();
              if (this.onResourceCategorySelect) {
                this.onResourceCategorySelect(sub);
              }
            });

            this.folderResourcesEl.appendChild(subItem);
          }
        }
      }
    }

    // ARCHIVES: Archived documents
    if (this.folderArchivesEl) {
      const archived = this.documents.filter(d => d.isArchived);
      this.folderArchivesEl.innerHTML = '';

      if (archived.length > 0) {
        for (const doc of archived) {
          const item = document.createElement('a');
          item.className = `para-folder-item${doc.id === this.activeDocId ? ' active' : ''}`;
          item.textContent = doc.title || 'Sin título';
          item.href = '#';
          item.onclick = (e) => { e.preventDefault(); if (this.onDocSelect) this.onDocSelect(doc); };
          this.folderArchivesEl.appendChild(item);
        }
      } else {
        this.folderArchivesEl.innerHTML = '<div class="para-folder-item" style="color: var(--text-muted); font-size: 12px;">Empty</div>';
      }
    }
  }

  // Render workspaces list
  public renderWorkspacesList(): void {
    if (!this.workspacesListEl) return;

    this.workspacesListEl.innerHTML = '';

    for (const ws of this.workspaces) {
      const isActive = ws.id === this.activeWorkspaceId;
      const item = document.createElement('div');
      item.className = `workspace-item${isActive ? ' active' : ''}`;
      item.innerHTML = `
        <span class="workspace-color" style="background: ${ws.color}"></span>
        <span class="workspace-name">${ws.name}</span>
      `;

      item.addEventListener('click', () => {
        this.setActiveWorkspace(ws.id);
      });

      this.workspacesListEl.appendChild(item);
    }

    // Add "new workspace" button
    const addItem = document.createElement('div');
    addItem.className = 'workspace-item workspace-add';
    addItem.innerHTML = `
      <span class="material-symbols-outlined">add</span>
      <span class="workspace-name">New workspace...</span>
    `;
    addItem.addEventListener('click', () => {
      const name = prompt('Workspace name:');
      if (name && name.trim()) {
        this.createWorkspace(name.trim());
      }
    });
    this.workspacesListEl.appendChild(addItem);
  }

  // Keep for backwards compatibility but redirect to renderWorkspacesList
  public renderActiveWorkspaces(_tabs: Array<{ id: string; title: string }>): void {
    this.renderWorkspacesList();
  }

  // Render hashtags as pills
  public renderHashtags(): void {
    if (!this.hashtagsListEl) return;

    this.hashtagsListEl.innerHTML = '';

    // Get unique tags from all documents
    const allTags = new Set<string>();
    for (const doc of this.documents) {
      if (doc.tags) {
        for (const tag of doc.tags) {
          allTags.add(tag);
        }
      }
    }

    const sortedTags = Array.from(allTags).sort();

    for (const tag of sortedTags.slice(0, 10)) { // Show max 10 tags
      const pill = document.createElement('span');
      pill.className = `hashtag${this.activeTag === tag ? ' active' : ''}`;
      pill.textContent = `#${tag}`;
      pill.addEventListener('click', () => {
        if (this.activeTag === tag) {
          this.activeTag = null;
          pill.classList.remove('active');
        } else {
          document.querySelectorAll('.hashtag').forEach(h => h.classList.remove('active'));
          this.activeTag = tag;
          pill.classList.add('active');
        }
        this.renderPARAFolders();
      });
      this.hashtagsListEl.appendChild(pill);
    }

    if (sortedTags.length === 0) {
      this.hashtagsListEl.innerHTML = '<span style="color: var(--text-muted); font-size: 11px;">No tags yet</span>';
    }
  }

  private renderSearchResults(): void {
    if (!this.searchResults || this.searchResults.length === 0) {
      const emptyEl = document.createElement('li');
      emptyEl.className = 'doc-item';
      emptyEl.innerHTML = '<span class="doc-item-title" style="color: var(--text-muted)">No se encontraron resultados</span>';
      this.docListEl.appendChild(emptyEl);
      return;
    }

    const header = document.createElement('div');
    header.className = 'section-header';
    header.textContent = `🔍 ${this.searchResults.length} resultado${this.searchResults.length !== 1 ? 's' : ''}`;
    this.docListEl.appendChild(header);

    for (const result of this.searchResults) {
      const li = document.createElement('li');
      li.className = 'doc-item search-result';
      li.dataset.id = result.id;

      // Check if this doc exists in our local list for additional info
      const localDoc = this.documents.find(d => d.id === result.id);
      const hasKnowledge = localDoc?.isKnowledgeEnabled === true;

      li.innerHTML = `
        <div class="doc-item-content">
          <div class="doc-item-title">${result.title}</div>
          <div class="search-result-snippet">${this.highlightQuery(result.snippet, this.searchQuery)}</div>
          ${result.tags.length > 0 ? `<div class="search-result-tags">${result.tags.map(t => `<span class="tag-mini">${t}</span>`).join('')}</div>` : ''}
        </div>
        ${hasKnowledge ? '<span class="doc-knowledge-indicator">🧠</span>' : ''}
      `;

      li.addEventListener('click', () => {
        if (localDoc && this.onDocSelect) {
          this.onDocSelect(localDoc);
        } else {
          // Fallback: create minimal doc object
          const minimalDoc: DocumentMeta = {
            id: result.id,
            title: result.title,
            tags: result.tags,
            created_at: Date.now(),
            updated_at: Date.now(),
            version: 1,
            source: 'local'
          };
          if (this.onDocSelect) {
            this.onDocSelect(minimalDoc);
          }
        }
      });

      this.docListEl.appendChild(li);
    }
  }

  private highlightQuery(text: string, query: string): string {
    if (!query || !text) return text || '';
    const truncated = text.length > 150 ? text.slice(0, 150) + '...' : text;
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return truncated.replace(regex, '<mark>$1</mark>');
  }

  private createDocItem(doc: DocumentMeta, nested: boolean = false): HTMLElement {
    const li = document.createElement('li');
    const hasKnowledge = doc.isKnowledgeEnabled === true;
    li.className = `doc-item${doc.id === this.activeDocId ? ' active' : ''}${nested ? ' nested' : ''}${hasKnowledge ? ' has-knowledge' : ''}`;
    li.dataset.id = doc.id;

    // Drag & Drop
    li.draggable = true;
    li.addEventListener('dragstart', (e) => {
      e.dataTransfer?.setData('text/plain', doc.id);
      li.classList.add('dragging');
    });
    li.addEventListener('dragend', () => {
      li.classList.remove('dragging');
    });

    // Icono documento
    const icon = document.createElement('span');
    icon.className = 'material-symbols-outlined doc-icon';
    icon.textContent = 'article';
    li.appendChild(icon);

    // Content wrapper
    const content = document.createElement('div');
    content.className = 'doc-item-content';

    const title = document.createElement('div');
    title.className = 'doc-item-title';
    title.textContent = doc.title || 'Sin título';
    title.title = 'Doble clic para renombrar';

    title.addEventListener('dblclick', (e) => {
      e.stopPropagation();
      const newTitle = prompt('Nuevo nombre:', doc.title);
      if (newTitle && newTitle.trim() && newTitle !== doc.title) {
        if (this.onDocRename) {
          this.onDocRename(doc.id, newTitle.trim());
        }
      }
    });

    content.appendChild(title);
    li.appendChild(content);

    // Knowledge indicator
    const knowledgeIndicator = document.createElement('span');
    knowledgeIndicator.className = 'doc-knowledge-indicator';
    knowledgeIndicator.textContent = hasKnowledge ? '🧠' : '';
    knowledgeIndicator.title = hasKnowledge ? 'In Knowledge Base' : '';
    li.appendChild(knowledgeIndicator);

    li.addEventListener('click', () => {
      if (this.onDocSelect) {
        this.onDocSelect(doc);
      }
    });

    // Menú contextual (clic derecho) con todas las opciones
    li.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      this.contextMenuManager.show(doc, e.clientX, e.clientY);
    });

    return li;
  }

  private renderTags(): void {
    this.tagsFilterEl.innerHTML = '';

    const allChip = document.createElement('span');
    allChip.className = `tag-chip${!this.activeTag ? ' active' : ''}`;
    allChip.textContent = 'Todos';
    allChip.addEventListener('click', () => {
      this.activeTag = null;
      this.renderTags();
      this.renderDocList();
    });
    this.tagsFilterEl.appendChild(allChip);

    for (const tag of this.tags) {
      const chip = document.createElement('span');
      chip.className = `tag-chip${tag === this.activeTag ? ' active' : ''}`;
      chip.textContent = tag;
      chip.addEventListener('click', () => {
        this.activeTag = tag === this.activeTag ? null : tag;
        this.renderTags();
        this.renderDocList();
      });
      this.tagsFilterEl.appendChild(chip);
    }
  }

  setActiveDoc(id: string | null): void {
    this.activeDocId = id;
    this.renderDocList();
    this.renderPARAFolders();
  }

  // Callbacks - forward to context menu manager
  onDocumentSelect(callback: DocSelectCallback): void {
    this.onDocSelect = callback;
  }

  onDocumentDelete(callback: DocDeleteCallback): void {
    this.onDocDelete = callback;
    this.contextMenuManager.setCallback('onDelete', callback);
  }

  onDocumentRename(callback: DocRenameCallback): void {
    this.onDocRename = callback;
    this.contextMenuManager.setCallback('onRename', callback);
  }

  onDocumentFavorite(callback: DocFavoriteCallback): void {
    this.onDocFavorite = callback;
    this.contextMenuManager.setCallback('onFavorite', callback);
  }

  onDocumentMove(callback: DocMoveCallback): void {
    this.onDocMove = callback;
    this.contextMenuManager.setCallback('onMove', callback);
  }

  onDocumentArchive(callback: DocArchiveCallback): void {
    this.onDocArchive = callback;
    this.contextMenuManager.setCallback('onArchive', callback);
  }

  onDocumentKnowledge(callback: DocKnowledgeCallback): void {
    this.onDocKnowledge = callback;
    this.contextMenuManager.setCallback('onKnowledge', callback);
  }

  onDocumentWorkspace(callback: DocWorkspaceCallback): void {
    this.onDocWorkspace = callback;
    this.contextMenuManager.setCallback('onWorkspace', callback);
  }

  onWorkspaceChangeCallback(callback: WorkspaceChangeCallback): void {
    this.onWorkspaceChange = callback;
  }

  onKnowledgeItemOpenCallback(callback: KnowledgeItemOpenCallback): void {
    this.onKnowledgeItemOpen = callback;
  }

  onProjectSelectCallback(callback: (projectName: string) => void): void {
    this.onProjectSelect = callback;
  }

  onResourceCategorySelectCallback(callback: (category: string) => void): void {
    this.onResourceCategorySelect = callback;
  }

  onIntentFilterCallback(callback: (intent: string | null) => void): void {
    this.onIntentFilter = callback;
  }

  // Get unique project names from all documents
  private getUniqueProjects(): string[] {
    const projects = new Set<string>();
    for (const doc of this.documents) {
      if (doc.project) {
        projects.add(doc.project);
      }
    }
    return Array.from(projects).sort();
  }

  // Get all documents
  getDocuments(): DocumentMeta[] {
    return this.documents;
  }

  // Workspace methods
  private renderWorkspaceSelector(): void {
    const container = document.getElementById('workspace-selector');
    if (!container) return;

    container.innerHTML = '';

    const select = document.createElement('select');
    select.className = 'workspace-select';

    for (const ws of this.workspaces) {
      const option = document.createElement('option');
      option.value = ws.id;
      option.textContent = ws.name;
      option.selected = ws.id === this.activeWorkspaceId;
      option.style.color = ws.color;
      select.appendChild(option);
    }

    // Opción para crear nuevo
    const newOption = document.createElement('option');
    newOption.value = '__new__';
    newOption.textContent = '+ Nuevo workspace...';
    select.appendChild(newOption);

    select.addEventListener('change', () => {
      if (select.value === '__new__') {
        const name = prompt('Nombre del workspace:');
        if (name && name.trim()) {
          this.createWorkspace(name.trim());
        }
        select.value = this.activeWorkspaceId;
      } else {
        this.setActiveWorkspace(select.value);
      }
    });

    container.appendChild(select);
  }

  private async createWorkspace(name: string): Promise<void> {
    const colors = ['#e94560', '#4ade80', '#60a5fa', '#f59e0b', '#a855f7'];
    const color = colors[this.workspaces.length % colors.length];

    const response = await api.createWorkspace(name, color);
    if (response.success && response.data) {
      this.workspaces.push(response.data);
      this.setActiveWorkspace(response.data.id);
    }
  }

  setActiveWorkspace(id: string): void {
    this.activeWorkspaceId = id;
    localStorage.setItem('notepad_active_workspace', id);
    this.renderWorkspaceSelector();
    this.renderWorkspacesList();
    this.renderDocList();

    if (this.onWorkspaceChange) {
      this.onWorkspaceChange(id);
    }
  }

  getActiveWorkspaceId(): string {
    return this.activeWorkspaceId;
  }

  getWorkspaces(): Workspace[] {
    return this.workspaces;
  }

  // Document management
  addDocument(doc: DocumentMeta): void {
    this.documents.unshift(doc);
    this.renderDocList();
  }

  updateDocument(doc: DocumentMeta): void {
    const index = this.documents.findIndex(d => d.id === doc.id);
    if (index >= 0) {
      this.documents[index] = doc;
      this.documents.splice(index, 1);
      this.documents.unshift(doc);
      this.renderDocList();
    }
  }

  removeDocument(id: string): void {
    this.documents = this.documents.filter(d => d.id !== id);
    this.renderDocList();
  }

  // Folder management
  addFolder(folder: Folder): void {
    this.folders.push(folder);
    this.renderDocList();
  }

  updateFolder(folder: Folder): void {
    const index = this.folders.findIndex(f => f.id === folder.id);
    if (index >= 0) {
      this.folders[index] = folder;
      this.renderDocList();
    }
  }

  removeFolder(id: string): void {
    this.folders = this.folders.filter(f => f.id !== id);
    this.expandedFolders.delete(id);
    this.saveExpandedFolders();
    this.renderDocList();
  }

  getFolders(): Folder[] {
    return this.folders;
  }

  toggleShowArchived(): boolean {
    this.showArchived = !this.showArchived;
    this.renderDocList();
    return this.showArchived;
  }

  isShowingArchived(): boolean {
    return this.showArchived;
  }

  getArchivedCount(): number {
    return this.documents.filter(d => d.isArchived).length;
  }

  // Related docs methods
  async loadRelatedDocs(docId: string): Promise<void> {
    // For notepad docs, check if knowledge is enabled
    // For external knowledge items (not in documents), always load related
    const doc = this.documents.find(d => d.id === docId);
    if (doc && !doc.isKnowledgeEnabled) {
      this.hideRelatedDocs();
      return;
    }

    try {
      const response = await resourcesApi.related(docId, 5);
      if (response.success && response.data && response.data.related.length > 0) {
        // Convert Resource[] to KnowledgeSearchResult[] for renderRelatedDocs
        this.relatedDocs = response.data.related.map((r: Resource) => ({
          score: 1, // Related doesn't return scores
          metadata: {
            docId: r.id,
            source: r.source || 'resource',
            title: r.title
          }
        }));
        this.renderRelatedDocs();
        this.showRelatedDocs();
      } else {
        this.hideRelatedDocs();
      }
    } catch (error) {
      console.error('Failed to load related docs:', error);
      this.hideRelatedDocs();
    }
  }

  private renderRelatedDocs(): void {
    this.relatedDocsListEl.innerHTML = '';

    for (const result of this.relatedDocs) {
      const li = document.createElement('li');
      li.className = 'related-doc-item';
      li.dataset.id = result.metadata.docId;
      li.dataset.source = result.metadata.source;

      const scorePercent = Math.round(result.score * 100);
      const title = result.metadata.title || 'Sin título';

      li.innerHTML = `
        <span class="related-doc-score">${scorePercent}%</span>
        <span class="related-doc-title" title="${title}">${title}</span>
        <span class="related-doc-source">${result.metadata.source}</span>
      `;

      li.addEventListener('click', async () => {
        if (result.metadata.source === 'notepad' && this.onDocSelect) {
          const doc = this.documents.find(d => d.id === result.metadata.docId);
          if (doc) {
            this.onDocSelect(doc);
          }
        } else if (this.onKnowledgeItemOpen) {
          // External sources - fetch and open in editor
          try {
            const response = await resourcesApi.get(result.metadata.docId);
            if (response.success && response.data) {
              // Convert Resource to KnowledgeItemDetail
              const item: KnowledgeItemDetail = {
                id: response.data.id,
                title: response.data.title,
                source: response.data.source || 'resource',
                content: response.data.content || undefined,
                excerpt: response.data.content?.slice(0, 150) || undefined,
                url: response.data.sourceUrl || undefined,
                categories: response.data.resourceCategory ? [response.data.resourceCategory] : [],
                projectId: response.data.project || undefined,
                updatedAt: response.data.updatedAt
              };
              this.onKnowledgeItemOpen(item);
            }
          } catch (error) {
            console.error('Failed to load item:', error);
          }
        }
      });

      this.relatedDocsListEl.appendChild(li);
    }
  }

  private showRelatedDocs(): void {
    this.relatedDocsPanelEl.classList.remove('hidden');
  }

  hideRelatedDocs(): void {
    this.relatedDocsPanelEl.classList.add('hidden');
    this.relatedDocs = [];
    this.relatedDocsListEl.innerHTML = '';
  }

  // Knowledge tab methods
  private setupKnowledgeTabListeners(): void {
    const refreshBtn = document.getElementById('btn-knowledge-refresh');
    if (refreshBtn) {
      refreshBtn.addEventListener('click', () => {
        this.loadKnowledgeItems(true);
      });
    }

    const searchInput = document.getElementById('knowledge-search-input') as HTMLInputElement;
    if (searchInput) {
      let searchTimeout: ReturnType<typeof setTimeout>;
      searchInput.addEventListener('input', () => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          this.renderKnowledgeList();
        }, 300);
      });
    }

    // Filter listeners
    const filters = ['filter-source', 'filter-category', 'filter-project', 'filter-intent'];
    filters.forEach(id => {
      const el = document.getElementById(id) as HTMLSelectElement;
      if (el) {
        el.addEventListener('change', () => this.renderKnowledgeList());
      }
    });
  }

  // Tree folder toggle (PARA folders)
  private setupTreeFolderListeners(): void {
    const folderHeaders = document.querySelectorAll('.tree-folder-header');
    folderHeaders.forEach(header => {
      header.addEventListener('click', () => {
        const folder = header.closest('.tree-folder');
        if (folder) {
          folder.classList.toggle('open');
        }
      });
    });
  }

  async loadKnowledgeItems(forceRefresh: boolean = false): Promise<void> {
    if (this.isLoadingKnowledge) return;
    if (!forceRefresh && this.knowledgeItems.length > 0) {
      this.renderKnowledgeList();
      return;
    }

    this.isLoadingKnowledge = true;
    this.renderKnowledgeList();

    try {
      const response = await resourcesApi.list({ limit: 100 });
      if (response.success && response.resources) {
        // Convert Resource[] to KnowledgeListItem[]
        this.knowledgeItems = response.resources.map(r => ({
          id: r.id,
          title: r.title,
          source: r.source || 'resource',
          excerpt: r.content?.slice(0, 150) || undefined,
          url: r.sourceUrl || undefined,
          categories: r.resourceCategory ? [r.resourceCategory] : [],
          projectId: r.project || undefined,
          intent: (r.customFields as Record<string, unknown>)?.intent as string | undefined,
          updatedAt: r.updatedAt
        }));
        this.populateKnowledgeFilters();
        this.updateIntentBadges();
      }
    } catch (error) {
      console.error('Failed to load knowledge items:', error);
      this.knowledgeItems = [];
    }

    this.isLoadingKnowledge = false;
    this.renderKnowledgeList();
  }

  private populateKnowledgeFilters(): void {
    // Collect unique values
    const sources = new Set<string>();
    const categories = new Set<string>();
    const projects = new Set<string>();

    for (const item of this.knowledgeItems) {
      if (item.source) sources.add(item.source);
      if (item.categories) item.categories.forEach(c => categories.add(c));
      if (item.projectId) projects.add(item.projectId);
    }

    // Populate source filter
    const sourceFilter = document.getElementById('filter-source') as HTMLSelectElement;
    if (sourceFilter) {
      const currentValue = sourceFilter.value;
      sourceFilter.innerHTML = '<option value="">All Sources</option>';
      Array.from(sources).sort().forEach(s => {
        const icon = this.getSourceIcon(s);
        sourceFilter.innerHTML += `<option value="${s}">${icon} ${s}</option>`;
      });
      sourceFilter.value = currentValue;
    }

    // Populate category filter
    const categoryFilter = document.getElementById('filter-category') as HTMLSelectElement;
    if (categoryFilter) {
      const currentValue = categoryFilter.value;
      categoryFilter.innerHTML = '<option value="">All Categories</option>';
      Array.from(categories).sort().forEach(c => {
        categoryFilter.innerHTML += `<option value="${c}">${c}</option>`;
      });
      categoryFilter.value = currentValue;
    }

    // Populate project filter
    const projectFilter = document.getElementById('filter-project') as HTMLSelectElement;
    if (projectFilter) {
      const currentValue = projectFilter.value;
      projectFilter.innerHTML = '<option value="">All Projects</option>';
      Array.from(projects).sort().forEach(p => {
        projectFilter.innerHTML += `<option value="${p}">${p}</option>`;
      });
      projectFilter.value = currentValue;
    }
  }

  private renderKnowledgeList(): void {
    const listEl = document.getElementById('knowledge-list');
    if (!listEl) return;

    listEl.innerHTML = '';

    if (this.isLoadingKnowledge) {
      listEl.innerHTML = '<div class="list-loading">Loading...</div>';
      return;
    }

    // Get filter values
    const searchInput = document.getElementById('knowledge-search-input') as HTMLInputElement;
    const sourceFilter = document.getElementById('filter-source') as HTMLSelectElement;
    const categoryFilter = document.getElementById('filter-category') as HTMLSelectElement;
    const projectFilter = document.getElementById('filter-project') as HTMLSelectElement;
    const intentFilter = document.getElementById('filter-intent') as HTMLSelectElement;

    const searchQuery = searchInput?.value.toLowerCase().trim() || '';
    const sourceValue = sourceFilter?.value || '';
    const categoryValue = categoryFilter?.value || '';
    const projectValue = projectFilter?.value || '';
    const intentValue = intentFilter?.value || '';

    let filtered = this.knowledgeItems;

    // Apply search
    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(searchQuery) ||
        (item.excerpt?.toLowerCase().includes(searchQuery))
      );
    }

    // Apply source filter
    if (sourceValue) {
      filtered = filtered.filter(item => item.source === sourceValue);
    }

    // Apply category filter
    if (categoryValue) {
      filtered = filtered.filter(item =>
        item.categories?.includes(categoryValue)
      );
    }

    // Apply project filter
    if (projectValue) {
      filtered = filtered.filter(item => item.projectId === projectValue);
    }

    // Apply intent filter
    if (intentValue) {
      filtered = filtered.filter(item => item.intent === intentValue);
    }

    // Show count
    const countEl = document.createElement('div');
    countEl.className = 'knowledge-count';
    countEl.textContent = `${filtered.length} items`;
    listEl.appendChild(countEl);

    if (filtered.length === 0) {
      listEl.innerHTML += '<div class="list-empty">No items found</div>';
      return;
    }

    for (const item of filtered) {
      listEl.appendChild(this.createKnowledgeItem(item));
    }
  }

  private createKnowledgeItem(item: KnowledgeListItem): HTMLElement {
    const itemEl = document.createElement('div');
    itemEl.className = 'knowledge-item';
    itemEl.dataset.id = item.id;
    itemEl.dataset.source = item.source;
    if (item.intent) itemEl.dataset.intent = item.intent;

    const sourceIcon = this.getSourceIcon(item.source);
    const dateStr = item.updatedAt
      ? new Date(item.updatedAt).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })
      : '';

    // Build badges
    const badges: string[] = [];
    if (item.categories?.length) {
      badges.push(...item.categories.slice(0, 2).map(c => `<span class="item-badge category-badge">${c}</span>`));
    }
    if (item.projectId) {
      badges.push(`<span class="item-badge project-badge">${item.projectId}</span>`);
    }

    itemEl.innerHTML = `
      <div class="item-header">
        <span class="item-icon">${sourceIcon}</span>
        <span class="item-title">${item.title}</span>
      </div>
      ${badges.length ? `<div class="item-badges">${badges.join('')}</div>` : ''}
      ${item.excerpt ? `<div class="item-excerpt">${item.excerpt.slice(0, 100)}${item.excerpt.length > 100 ? '...' : ''}</div>` : ''}
      <div class="item-meta">
        <span class="item-source">${item.source}</span>
        ${dateStr ? `<span class="item-date">${dateStr}</span>` : ''}
      </div>
    `;

    // Click behavior depends on source
    itemEl.addEventListener('click', async () => {
      // For notepad items, open directly
      if (item.source === 'notepad') {
        const doc = this.documents.find(d => d.id === item.id);
        if (doc && this.onDocSelect) {
          this.onDocSelect(doc);
        }
        return;
      }

      // For external items with URL, open in new tab
      if (item.url) {
        window.open(item.url, '_blank');
        return;
      }

      // Otherwise fetch content and open in editor
      if (this.onKnowledgeItemOpen) {
        try {
          const response = await resourcesApi.get(item.id);
          if (response.success && response.data) {
            // Convert Resource to KnowledgeItemDetail
            const detail: KnowledgeItemDetail = {
              id: response.data.id,
              title: response.data.title,
              source: response.data.source || 'resource',
              content: response.data.content || undefined,
              excerpt: response.data.content?.slice(0, 150) || undefined,
              url: response.data.sourceUrl || undefined,
              categories: response.data.resourceCategory ? [response.data.resourceCategory] : [],
              projectId: response.data.project || undefined,
              updatedAt: response.data.updatedAt
            };
            this.onKnowledgeItemOpen(detail);
          }
        } catch (error) {
          console.error('Failed to load item:', error);
        }
      }
    });

    return itemEl;
  }

  private getSourceIcon(source: string): string {
    const icons: Record<string, string> = {
      'notepad': '📝',
      'gdrive': '📁',
      'youtube': '🎬',
      'uniscribe': '🎙️',
      'omi': '🎤',
      'instagram': '📷',
      'tiktok': '🎵',
      'browser': '🌐',
      'n8n': '⚡',
      'voicenotes': '🗣️',
      'hidoc': '📄'
    };
    return icons[source] || '📄';
  }

}
